// User management utility for Connect4 game
// Handles saving, loading, and managing user data in localStorage

const USER_STORAGE_KEY = 'connect4-user';

const defaultUserData = {
  username: '',
  joinDate: null,
  totalGamesPlayed: 0,
  totalTimeSpent: 0, // in seconds
  lastPlayed: null,
  preferences: {
    theme: 'default',
    showAnimations: true
  }
};

// Load user data from localStorage
export const loadUserData = () => {
  try {
    const savedUserData = localStorage.getItem(USER_STORAGE_KEY);
    if (savedUserData) {
      const parsed = JSON.parse(savedUserData);
      // Merge with default user data to ensure all properties exist
      return {
        ...defaultUserData,
        ...parsed,
        preferences: { ...defaultUserData.preferences, ...parsed.preferences }
      };
    }
  } catch (error) {
    console.error('Error loading user data from localStorage:', error);
  }
  return defaultUserData;
};

// Save user data to localStorage
export const saveUserData = (userData) => {
  try {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
  } catch (error) {
    console.error('Error saving user data to localStorage:', error);
  }
};

// Get current username
export const getCurrentUsername = () => {
  const userData = loadUserData();
  return userData.username;
};

// Set username (first time setup)
export const setUsername = (username) => {
  const userData = loadUserData();
  const now = new Date().toISOString();
  
  const updatedUserData = {
    ...userData,
    username: username.trim(),
    joinDate: userData.joinDate || now, // Keep existing join date if already set
    lastPlayed: now
  };
  
  saveUserData(updatedUserData);
  return updatedUserData;
};

// Check if user has set a username
export const hasUsername = () => {
  const userData = loadUserData();
  return userData.username && userData.username.trim().length > 0;
};

// Update user stats after a game
export const updateUserStats = (gameTime) => {
  const userData = loadUserData();
  
  const updatedUserData = {
    ...userData,
    totalGamesPlayed: userData.totalGamesPlayed + 1,
    totalTimeSpent: userData.totalTimeSpent + gameTime,
    lastPlayed: new Date().toISOString()
  };
  
  saveUserData(updatedUserData);
  return updatedUserData;
};

// Update user preferences
export const updateUserPreferences = (preferences) => {
  const userData = loadUserData();
  
  const updatedUserData = {
    ...userData,
    preferences: { ...userData.preferences, ...preferences }
  };
  
  saveUserData(updatedUserData);
  return updatedUserData;
};

// Get user display data for statistics
export const getUserDisplayData = () => {
  const userData = loadUserData();
  
  return {
    username: userData.username,
    joinDate: userData.joinDate,
    totalGamesPlayed: userData.totalGamesPlayed,
    totalTimeSpent: userData.totalTimeSpent,
    lastPlayed: userData.lastPlayed,
    memberSince: userData.joinDate ? new Date(userData.joinDate).toLocaleDateString() : 'Unknown'
  };
};

// Reset all user data (for development/testing)
export const resetUserData = () => {
  try {
    localStorage.removeItem(USER_STORAGE_KEY);
    return defaultUserData;
  } catch (error) {
    console.error('Error resetting user data:', error);
    return defaultUserData;
  }
};

// Validate username
export const validateUsername = (username) => {
  const trimmed = username.trim();
  
  if (!trimmed) {
    return { valid: false, error: 'Please enter a username' };
  }
  
  if (trimmed.length < 2) {
    return { valid: false, error: 'Username must be at least 2 characters long' };
  }
  
  if (trimmed.length > 20) {
    return { valid: false, error: 'Username must be 20 characters or less' };
  }
  
  // Check for valid characters (letters, numbers, spaces, dashes, underscores)
  const validPattern = /^[a-zA-Z0-9\s\-_]+$/;
  if (!validPattern.test(trimmed)) {
    return { 
      valid: false, 
      error: 'Username can only contain letters, numbers, spaces, dashes, and underscores' 
    };
  }
  
  return { valid: true, error: null };
};

// Format time for display
export const formatUserTime = (seconds) => {
  if (!seconds || seconds === 0) return '0 minutes';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes} minutes`;
  }
};
